import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { IoDocumentText } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import { MdInsertPhoto, MdPictureAsPdf } from "react-icons/md";

import { BackFromCustomerDetailIcon, ViewIcon } from "../../../assets";
import {
  SCREEN_PATH,
  EMPTY_STRING,
  PAYMENT_DISABLED,
  adminReduxConstants,
  preferredContactMethod,
} from "../../../constants";
import { IMAGE_TYPES } from "../../../system";
import {
  formatRating,
  getLabelsByIds,
  formatSubscriptionDetails,
} from "../../../shared/utility";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions, countryAction } from "../../../redux/actions";

const { GET_AGENTS } = adminReduxConstants;

const AgentDetails = (props) => {
  const {
    agentDetails,
    getAgentDetails,
    allLanguages,
    getAllLanguages,
    spinnerActions,
    allCountries,
  } = props;
  const navigate = useNavigate();
  const { id } = useParams();
  const navigateToAgentListing = () => {
    navigate(`${SCREEN_PATH.ADMIN_AGENTS}/${id}/properties`);
  };
  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  useEffect(() => {
    getAllLanguages();
    getAgentDetails(id);

    // eslint-disable-next-line
  }, [id]);

  const getDocumentIcon = (document) => {
    const extension = document.image.split(".").pop().toLowerCase();

    const iconMap = {
      doc: <IoDocumentText size={20} />,
      docx: <IoDocumentText size={20} />,
      pdf: <MdPictureAsPdf size={20} />,
    };

    return iconMap[extension] || <MdInsertPhoto size={20} />;
  };

  return (
    <div className="agent-info-wrapper">
      {spinner(GET_AGENTS) > -1 && <MainSpinner className="admin-spinner" />}
      <MainContentWrapper className="position-relative">
        <img
          src={BackFromCustomerDetailIcon}
          className="customer-detail-back-icon pointer"
          onClick={() => navigate("/admin/users/agents", { replace: true })}
          alt={EMPTY_STRING}
        />
        <div className="row">
          <div className="col-12 col-lg-8">
            <div className="admin-page-heading">Agents</div>
            <div className="agent-details-container">
              <div className="d-flex gap-3">
                <div className="agent-image">
                  <img
                    src={
                      agentDetails?.images?.filter(
                        (img) => img.imageType === IMAGE_TYPES.TYPE_HEADSHOT
                      )[0].image
                    }
                    alt={EMPTY_STRING}
                  />
                </div>
                <div className="d-flex flex-column justify-content-between align-items-start">
                  <div className="name mt-1">
                    {agentDetails.firstName} {agentDetails.lastName}
                  </div>
                  <div>
                    <div className="d-flex gap-1">
                      <div className="rating-label">Rating:</div>
                      <div className="rating-value">
                        {formatRating(agentDetails?.rating)}
                      </div>
                    </div>
                    <div className="d-flex gap-1">
                      <div className="rating-label">Properties Listed:</div>
                      <div className="rating-value">
                        {agentDetails?.propertiesCount}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Subscription Info Section */}
          <div className="col-12 col-lg-4 mt-2 mt-lg-0 text-start text-lg-end">
            <div className="subscription-heading">Subscription</div>
            <div className="subscription-description">
              {agentDetails?.isPaymentDisabled
                ? PAYMENT_DISABLED
                : formatSubscriptionDetails(agentDetails?.subscriptionPlan)}
            </div>
          </div>
        </div>

        <div className="agent-details-container mt-0">
          <div className="agent-info w-100">
            <div className="agent-text-info">
              <div className="agent-info-heading">
                Personal Information
                <div className="rounded-border" />
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">First Name</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">{agentDetails.firstName}</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Last Name</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">{agentDetails.lastName}</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Phone Number</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">
                        +{agentDetails.phoneNumber}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Email Address</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">{agentDetails.email}</div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Preferred Contact Method</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">
                        {getLabelsByIds(
                          preferredContactMethod,
                          agentDetails?.preferredContactMethod,
                          "name"
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Languages Spoken</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">
                        {getLabelsByIds(
                          allLanguages,
                          agentDetails?.languageSpokenIds,
                          "name"
                        )?.map(
                          (instance, index) =>
                            instance +
                            `${
                              getLabelsByIds(
                                allLanguages,
                                agentDetails?.languageSpokenIds,
                                "name"
                              ).length ===
                              index + 1
                                ? ""
                                : ", "
                            }`
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="agent-info-heading agent-info-sub-heading mt-4">
                Emergency Contact Information
              </div>
              <div className="row">
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Full Name</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">
                        {agentDetails?.emergencyContactInfo?.fullName}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6">
                  <div className="row">
                    <div className="col-6">
                      <div className="info-label">Phone Number</div>
                    </div>
                    <div className="col-6">
                      <div className="info-value">
                        +{agentDetails?.emergencyContactInfo?.phoneNumber}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <div className="agent-info-heading">
                  Company Information
                  <div className="rounded-border" />
                </div>
                <div className="row">
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">Company/Agency Name</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          {agentDetails?.company?.name}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">Phone Number</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          +{agentDetails?.company?.phoneNumber}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">Licence Number</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          {agentDetails?.company?.licenseNumber}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">Website URL</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          {agentDetails?.company?.websiteUrl}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      <div className="col-6 col-sm-6">
                        <div className="info-label">Office Address Line 1</div>
                      </div>
                      <div className="col-6 col-sm-6">
                        <div className="info-value">
                          {agentDetails?.company?.addressLine1}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      <div className="col-6 col-sm-6">
                        <div className="info-label">Office Address Line 2</div>
                      </div>
                      <div className="col-6 col-sm-6">
                        <div className="info-value">
                          {agentDetails?.company?.addressLine2 ?? "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">Country:</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          {getLabelsByIds(
                            allCountries,
                            agentDetails?.company?.countryId,
                            "name"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">State:</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          {agentDetails?.company?.state}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="row">
                      <div className="col-6">
                        <div className="info-label">City:</div>
                      </div>
                      <div className="col-6">
                        <div className="info-value">
                          {agentDetails?.company?.city}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="agent-info-heading agent-info-sub-heading mt-4">
                  Office Hours
                </div>
                <div className="row">
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      {(
                        agentDetails?.company?.officeHours?.filter(
                          (schedule) => schedule.isActive
                        ) ?? []
                      )
                        .slice(0, 4)
                        .map((day) => (
                          <>
                            <div className="col-6">
                              <div className="info-label">{day.name}</div>
                            </div>
                            <div className="col-6">
                              <div className="info-value">
                                {day.startTime} - {day.endTime}
                              </div>
                            </div>
                          </>
                        ))}
                    </div>
                  </div>
                  <div className="col-12 col-sm-6">
                    <div className="row">
                      {(
                        agentDetails?.company?.officeHours?.filter(
                          (schedule) => schedule.isActive
                        ) ?? []
                      )
                        .slice(4)
                        .map((day) => (
                          <>
                            <div className="col-6">
                              <div className="info-label">{day.name}</div>
                            </div>
                            <div className="col-6">
                              <div className="info-value">
                                {day.startTime} - {day.endTime}
                              </div>
                            </div>
                          </>
                        ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="agent-graphics-info">
              <div className="row">
                <div className="col-12">
                  <div className="agent-info-heading agent-info-sub-heading">
                    Photo ID
                    <div className="rounded-border" />
                  </div>
                  <div className="photo-ids-container w-100 mt-2">
                    {agentDetails?.images
                      ?.filter(
                        (image) => image.imageType === IMAGE_TYPES.TYPE_PHOTO_ID
                      )
                      ?.map((img, index) => (
                        <div className="d-flex gap-2 justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <MdInsertPhoto size={20} />
                            <div className="img-name">{`photoId${index + 1}.${
                              img.image.split(".")[
                                img.image.split(".").length - 1
                              ]
                            }`}</div>
                          </div>
                          <a href={img.image} target="_blank">
                            <img src={ViewIcon} alt="" />
                          </a>
                        </div>
                      ))}
                  </div>
                </div>
                <div className="col-12">
                  <div className="agent-info-heading agent-info-sub-heading">
                    Registration Certificate
                    <div className="rounded-border" />
                  </div>
                  <div className="photo-ids-container w-100 mt-2">
                    {agentDetails?.images
                      ?.filter(
                        (image) => image.imageType === IMAGE_TYPES.TYPE_DOCUMENT
                      )
                      ?.map((document, index) => (
                        <div className="d-flex gap-2 justify-content-between align-items-center mb-2">
                          <div className="d-flex align-items-center gap-2">
                            {getDocumentIcon(document)}
                            <div className="img-name">{`document.${
                              document.image.split(".")[
                                document.image.split(".").length - 1
                              ]
                            }`}</div>
                          </div>
                          <a href={document.image} target="_blank">
                            <img src={ViewIcon} alt="" />
                          </a>
                        </div>
                      ))}
                  </div>
                </div>
                <div className="col-12 mt-3">
                  <div className="agent-info-heading agent-info-sub-heading">
                    Properties
                    <div className="rounded-border" />
                  </div>
                  <div className="d-flex gap-2 justify-content-between align-items-center">
                    <div className="img-name">
                      {agentDetails?.propertiesCount} Listings
                    </div>
                    <div
                      className="view-all-listings"
                      onClick={navigateToAgentListing}
                    >
                      View All
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainContentWrapper>
    </div>
  );
};

const mapStateToProps = (state) => ({
  agentDetails: state.adminReducer.agentDetails,
  allLanguages: state.countryReducer.allLanguages,
  spinnerActions: state.systemReducer.spinnerActions,
  allCountries: state.countryReducer.allCountries,
});

const mapDispatchToProps = (dispatch) => ({
  getAgentDetails: (id) => dispatch(adminActions.getAgentDetails(id)),
  getAllLanguages: () => dispatch(countryAction.getAllLanguages()),
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentDetails);

// export default AgentDetails
